import { Card, CardEmpty, EmptyState, Rows, Row, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useShows } from "../../app/hooks/useShows.js";
import { useMerchRollup } from "../../app/hooks/useMerchRollup.js";
import { useProduction } from "../../app/hooks/useProduction.js";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";
import styles from "./RoleCards.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];

/**
 * The companion cards that sit under each role hero (the reference's second main-column slot). Like the
 * heroes, placement is the server's call (`nav.dashboard.main`); these only render.
 */

/** Today in device-local wall-clock (`YYYY-MM-DD`). */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "Aug 16 · Nashville" — the row sub-line, no timezone shift. */
function whenWhere(show: ShowSummary): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(show.date ?? "");
  const when = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
        month: "short", day: "numeric",
      })
    : null;
  return [when, show.city].filter(Boolean).join(" · ");
}

// ---- band member: recent shows ------------------------------------------------------------------

/**
 * The band member's played dates — the last five, newest first. The reference makes each row a door to
 * that show's media; the media link was retired 2026-08-29 (its only writer went with the show editor's
 * Assets rework), so a row names the date and place and leaves the show page to carry everything else.
 */
export function RecentShowsCard() {
  const q = useShows({ status: "confirmed", sort: "-date", to: today() });
  const rows = (q.data?.items ?? []).slice(0, 5);

  return (
    <Card id="recent-shows" title="Recent shows" flush>
      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading shows…" />
        </div>
      ) : rows.length === 0 ? (
        <CardEmpty>No shows played yet — your history builds here.</CardEmpty>
      ) : (
        <Rows>
          {rows.map((show, i) => (
            <Row
              key={show.showId}
              avatar={<span className={styles.rank} aria-hidden="true">#{i + 1}</span>}
              name={show.venueName || "Venue TBA"}
              sub={whenWhere(show)}
            />
          ))}
        </Rows>
      )}
    </Card>
  );
}

// ---- tour manager: next 3 + top merch -----------------------------------------------------------

/**
 * The TM's two-column companion: the next three dates with their capacity, and the store's top sellers
 * for the last 30 days (what to load into the van tonight).
 */
export function TmNext3Card() {
  const shows = useShows({ status: "confirmed", sort: "date", from: today() });
  const merch = useMerchRollup();
  const next3 = (shows.data?.items ?? []).slice(0, 3);
  const top = (merch.data?.rollup?.bestSellers ?? []).slice(0, 3);

  return (
    <div className={styles.split}>
      <Card id="tm-next-3" title="Next 3" flush>
        {shows.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading shows…" />
          </div>
        ) : next3.length === 0 ? (
          <CardEmpty>No upcoming shows yet.</CardEmpty>
        ) : (
          <Rows>
            {next3.map((show) => (
              <Row
                key={show.showId}
                name={show.venueName || "Venue TBA"}
                sub={whenWhere(show)}
                trailing={
                  show.capacity != null ? (
                    <span className={styles.cap}>Cap {show.capacity}</span>
                  ) : undefined
                }
              />
            ))}
          </Rows>
        )}
      </Card>

      <Card id="tm-top-merch" title="Top merch · 30d" flush>
        {merch.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading merch…" />
          </div>
        ) : top.length === 0 ? (
          <CardEmpty>No sales in the last 30 days.</CardEmpty>
        ) : (
          <Rows>
            {top.map((item, i) => (
              <Row
                key={`${item.title}-${i}`}
                avatar={<span className={styles.rank} aria-hidden="true">{i + 1}</span>}
                name={item.title ?? "Product"}
                sub={item.units != null ? `${item.units} units` : undefined}
                trailing={
                  item.grossCents != null ? (
                    <span className={styles.money}>{formatMoneyCents(item.grossCents)}</span>
                  ) : undefined
                }
              />
            ))}
          </Rows>
        )}
      </Card>
    </div>
  );
}

// ---- production trio: the input list -------------------------------------------------------------

/**
 * The production seat's companion: the band's own channel list from the advance pack — the desktop twin of
 * the mobile home's Input List card, off the same `production:read` document.
 *
 * This slot holds the venue-spec strip in the reference. That directory doesn't exist here (see
 * {@link ProductionSpotlight}), and a card of five permanent dashes is worse than no card — so the slot
 * carries the seat's real artifact instead. Unlike the specs, this one has somewhere to be filled in:
 * the empty state names it.
 */
export function ProdInputListCard() {
  const q = useProduction();
  const rows = (q.data?.channels ?? []).slice(0, 8);

  return (
    <Card id="prod-input-list" title="Input list" flush>
      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading input list…" />
        </div>
      ) : q.isError ? (
        <EmptyState title="Couldn't load the input list" description="Please try again." />
      ) : rows.length === 0 ? (
        <CardEmpty>No input list yet — build the channel list in Advance.</CardEmpty>
      ) : (
        <Rows>
          {rows.map((c) => (
            <Row
              key={c.channelNo}
              avatar={<span className={styles.rank} aria-hidden="true">{c.channelNo}</span>}
              name={c.instrument || "Channel"}
              sub={c.micLabel || c.micCustom || undefined}
              trailing={
                c.status ? (
                  <span className={styles.cap}>
                    {c.status === "traveling_with" ? "Traveling with" : "Need"}
                  </span>
                ) : undefined
              }
            />
          ))}
        </Rows>
      )}
    </Card>
  );
}

// ---- merch manager: best sellers ----------------------------------------------------------------

/** The merch seat's top five products for the last 30 days — units and gross, straight off the rollup. */
export function MerchBestsellersCard() {
  const q = useMerchRollup();
  const rows = (q.data?.rollup?.bestSellers ?? []).slice(0, 5);
  const connected = q.data?.connected === true;

  return (
    <Card id="merch-bestsellers" title="Best sellers" flush>
      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading merch…" />
        </div>
      ) : q.isError ? (
        <EmptyState title="Couldn't load merch" description="Please try again." />
      ) : rows.length === 0 ? (
        <CardEmpty>
          {connected ? "No sales in the last 30 days." : "Connect your store to rank what's selling."}
        </CardEmpty>
      ) : (
        <Rows>
          {rows.map((item, i) => (
            <Row
              key={`${item.title}-${i}`}
              avatar={<span className={styles.rank} aria-hidden="true">#{i + 1}</span>}
              name={item.title ?? "Product"}
              sub={item.units != null ? `${item.units} units` : undefined}
              trailing={
                item.grossCents != null ? (
                  <span className={styles.money}>{formatMoneyCents(item.grossCents)}</span>
                ) : undefined
              }
            />
          ))}
        </Rows>
      )}
    </Card>
  );
}
